import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { CountryCode } from '@data/shopify/storefront/types'
import {
  compareNumbers,
  filterDuplicates,
  useLocalStorageState,
} from './helpers'
import { getPageUrl, PageType } from './routes'
import { SiteContext } from './site'

export type Locale = 'da'

export const defaultLocale = 'da'

export type LocaleValues<T> = {
  [P in Locale]: T
}

export const localeNames: LocaleValues<string> = {
  da: 'Dansk',
}

interface LanguageContextProps {
  locale: Locale
  publicLocales: Locale[]
  changeLanguage: (newLocale: Locale) => void
}

const initialContext: LanguageContextProps = {
  locale: defaultLocale,
  publicLocales: [],
  changeLanguage: () => null,
}

export const LanguageContext =
  createContext<LanguageContextProps>(initialContext)

interface LanguageContextProviderProps {
  locale: Locale
  site: SanitySiteFragment
  children: ReactNode
}

export const LanguageContextProvider = ({
  locale,
  site,
  children,
}: LanguageContextProviderProps) => {
  const router = useRouter()
  const { toggleMegaNavigation, toggleMobileMenu } = useContext(SiteContext)

  // Handle language change
  const changeLanguage = useCallback(
    (newLocale: Locale) => {
      toggleMegaNavigation(false)
      toggleMobileMenu(false)

      if (locale !== newLocale) {
        const homeUrl = getPageUrl(PageType.HOME_PAGE)
        router.push(homeUrl, homeUrl, { locale: newLocale })
      }
    },
    [locale, router, toggleMegaNavigation, toggleMobileMenu],
  )

  return (
    <LanguageContext.Provider
      value={{
        locale,
        publicLocales: site.publicLocales as Locale[],
        changeLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

/**
 * Gets all locales.
 */
export const getAllLocales = () => {
  const allLocales: Locale[] = ['da']

  return allLocales
}

/*  ------------------------------ */
/*  Environment variables
/*  ------------------------------ */

type EnvironmentVariableName =
  | 'SANITY_PROJECT_ID'
  | 'SANITY_PROJECT_DATASET'
  | 'SANITY_API_TOKEN'
  | 'SANITY_PREVIEW_TOKEN'
  | 'SHOPIFY_STORE_ID'
  | 'SHOPIFY_API_TOKEN'
  | 'SHOPIFY_API_PASSWORD'
  | 'SHOPIFY_WEBHOOK_INTEGRITY'
  | 'SHOPIFY_CARRIER_SERVICE_TOKEN'
  | 'REPORT_URI_SUBDOMAIN'
  | 'MAILCHIMP_API_KEY'
  | 'MAILCHIMP_SERVER'
  | 'FACEBOOK_CONVERSIONS_API_TOKEN'

type EnvironmentVariableMap = Record<
  Locale | 'default',
  Record<EnvironmentVariableName, string | undefined>
>

const environmentVariables: EnvironmentVariableMap = {
  default: {
    SANITY_PROJECT_ID: process.env.SANITY_PROJECT_ID,
    SANITY_PROJECT_DATASET: process.env.SANITY_PROJECT_DATASET,
    SANITY_API_TOKEN: process.env.SANITY_API_TOKEN,
    SANITY_PREVIEW_TOKEN: process.env.SANITY_PREVIEW_TOKEN,
    SHOPIFY_STORE_ID: process.env.SHOPIFY_STORE_ID,
    SHOPIFY_API_TOKEN: process.env.SHOPIFY_API_TOKEN,
    SHOPIFY_API_PASSWORD: process.env.SHOPIFY_API_PASSWORD,
    SHOPIFY_WEBHOOK_INTEGRITY: process.env.SHOPIFY_WEBHOOK_INTEGRITY,
    SHOPIFY_CARRIER_SERVICE_TOKEN: process.env.SHOPIFY_CARRIER_SERVICE_TOKEN,
    REPORT_URI_SUBDOMAIN: process.env.REPORT_URI_SUBDOMAIN,
    MAILCHIMP_API_KEY: process.env.MAILCHIMP_API_KEY,
    MAILCHIMP_SERVER: process.env.MAILCHIMP_SERVER,
    FACEBOOK_CONVERSIONS_API_TOKEN: process.env.FACEBOOK_CONVERSIONS_API_TOKEN,
  },
  da: {
    SANITY_PROJECT_ID: process.env.SANITY_PROJECT_ID_DA,
    SANITY_PROJECT_DATASET: process.env.SANITY_PROJECT_DATASET_DA,
    SANITY_API_TOKEN: process.env.SANITY_API_TOKEN_DA,
    SANITY_PREVIEW_TOKEN: process.env.SANITY_PREVIEW_TOKEN_DA,
    SHOPIFY_STORE_ID: process.env.SHOPIFY_STORE_ID_DA,
    SHOPIFY_API_TOKEN: process.env.SHOPIFY_API_TOKEN_DA,
    SHOPIFY_API_PASSWORD: process.env.SHOPIFY_API_PASSWORD_DA,
    SHOPIFY_WEBHOOK_INTEGRITY: process.env.SHOPIFY_WEBHOOK_INTEGRITY_DA,
    SHOPIFY_CARRIER_SERVICE_TOKEN: process.env.SHOPIFY_CARRIER_SERVICE_TOKEN_DA,
    REPORT_URI_SUBDOMAIN: process.env.REPORT_URI_SUBDOMAIN_DA,
    MAILCHIMP_API_KEY: process.env.MAILCHIMP_API_KEY_DA,
    MAILCHIMP_SERVER: process.env.MAILCHIMP_SERVER_DA,
    FACEBOOK_CONVERSIONS_API_TOKEN:
      process.env.FACEBOOK_CONVERSIONS_API_TOKEN_DA,
  },
}

/**
 * Gets environment variable for specific locale.
 */
export const getLocaleVariable = (
  locale: Locale,
  key: EnvironmentVariableName,
) => environmentVariables[locale]?.[key] ?? environmentVariables.default[key]

/**
 * Gets all locales that use a Shopify store.
 */
export const getShopifyStoreLocales = (shopifyStoreId: string) => {
  const locales = getAllLocales()

  return locales.filter(
    (locale) =>
      getLocaleVariable(locale, 'SHOPIFY_STORE_ID') === shopifyStoreId,
  )
}

/*  ------------------------------ */
/*  Locale detection
/*  ------------------------------ */

/**
 * Returns user's locales as IETF language tags, based on all available sources.
 * Source: https://github.com/wojtekmaj/get-user-locale
 */
const getBrowserLocales = () => {
  if (typeof window === 'undefined') {
    return [defaultLocale]
  }

  let locales: string[] = []

  if (window.navigator.languages) {
    locales = locales.concat(window.navigator.languages)
  }

  if (window.navigator.language) {
    locales.push(window.navigator.language)
  }

  if (window.navigator.userLanguage) {
    locales.push(window.navigator.userLanguage)
  }

  if (window.navigator.browserLanguage) {
    locales.push(window.navigator.browserLanguage)
  }

  if (window.navigator.systemLanguage) {
    locales.push(window.navigator.systemLanguage)
  }

  locales.push(defaultLocale)

  return locales
    .filter((locale, index, array) => filterDuplicates(locale, index, array))
    .map((locale) => getNormalizedLocale(locale))
}

/**
 * Gets normalized locale by fixing capitalization.
 */
export const getNormalizedLocale = (locale: string) => {
  if (
    !locale ||
    locale.indexOf('-') === -1 ||
    locale.toLowerCase() !== locale
  ) {
    return locale
  }

  const localeParts = locale.split('-')

  return `${localeParts[0]}-${localeParts[1].toUpperCase()}`
}

/**
 * Finds the best matching site locale from all site and browser locales.
 */
export const getMatchingSiteLocale = (
  siteLocales: Locale[],
  browserLocales: string[],
  defaultSiteLocale: Locale,
) => {
  const matches = siteLocales
    .map((siteLocale) => {
      const exactIndex = browserLocales.indexOf(siteLocale)
      const partialIndex = browserLocales
        .map((locale) => locale.split('-')[0])
        .indexOf(siteLocale.split('-')[0])

      return {
        locale: siteLocale,
        index: exactIndex > -1 ? exactIndex : partialIndex,
      }
    })
    .filter(({ index }) => index > -1)
    .sort((match1, match2) => compareNumbers(match1.index, match2.index))

  if (matches.length > 0) {
    return matches[0].locale
  }

  return defaultSiteLocale
}

/**
 * Visitor's preferred locale detection hook.
 * Returns preferred locale (if it doesn't match site's locale) and a method to confirm current locale.
 */
export const usePreferredLocale = (locale: Locale) => {
  const [preferredLocale, setPreferredLocale] = useState<Locale | null>(null)
  const [browserLocales, setBrowserLocales] = useState<string[]>([])
  const [isLocaleConfirmed, setIsLocaleConfirmed] =
    useLocalStorageState<boolean>('is_locale_confirmed', false)

  // Detect browser locales
  useEffect(() => {
    if (isLocaleConfirmed) {
      return
    }

    const newBrowserLocales = getBrowserLocales()

    setBrowserLocales(newBrowserLocales)
  }, [isLocaleConfirmed])

  // Find best matching site locale based on browser locales
  useEffect(() => {
    if (isLocaleConfirmed || browserLocales.length === 0) {
      setPreferredLocale(null)

      return
    }

    const siteLocales = getAllLocales()
    const matchingSiteLocale = getMatchingSiteLocale(
      siteLocales,
      browserLocales,
      defaultLocale,
    )

    // Save preferred locale if it's not already being used
    setPreferredLocale(
      matchingSiteLocale !== locale ? matchingSiteLocale : null,
    )
  }, [browserLocales, isLocaleConfirmed, locale])

  const confirmLocale = useCallback(() => {
    if (!isLocaleConfirmed) {
      setIsLocaleConfirmed(true)
    }
  }, [isLocaleConfirmed, setIsLocaleConfirmed])

  return [preferredLocale, confirmLocale] as const
}

export const defaultCountryCodes: LocaleValues<CountryCode> = {
  da: CountryCode.Dk,
}

type CountryNames = Record<CountryCode, string>

export const countryNames: LocaleValues<CountryNames> = {
  da: {
    AC: 'Ascension Island',
    AD: 'Andorra',
    AE: 'De Forenede Arabiske Emirater',
    AF: 'Afghanistan',
    AG: 'Antigua og Barbuda',
    AI: 'Anguilla',
    AL: 'Albanien',
    AM: 'Armenien',
    AN: 'De Nederlandske Antiller',
    AO: 'Angola',
    AR: 'Argentina',
    AT: 'Østrig',
    AU: 'Australien',
    AW: 'Aruba',
    AX: 'Ålandsøerne',
    AZ: 'Aserbajdsjan',
    BA: 'Bosnien-Hercegovina',
    BB: 'Barbados',
    BD: 'Bangladesh',
    BE: 'Belgien',
    BF: 'Burkina Faso',
    BG: 'Bulgarien',
    BH: 'Bahrain',
    BI: 'Burundi',
    BJ: 'Benin',
    BL: 'St. Barthélemy',
    BM: 'Bermuda',
    BN: 'Brunei',
    BO: 'Bolivia',
    BQ: 'Caribbean Netherlands',
    BR: 'Brasilien',
    BS: 'Bahamas',
    BT: 'Bhutan',
    BV: 'Bouvetø',
    BW: 'Botswana',
    BY: 'Belarus',
    BZ: 'Belize',
    CA: 'Canada',
    CC: 'Cocosøerne (Keelingøerne)',
    CD: 'Demokratiske Republik Congo',
    CF: 'Den Centralafrikanske Republik',
    CG: 'Congo',
    CH: 'Schweiz',
    CI: `Côte d'Ivoire`,
    CK: 'Cookøerne',
    CL: 'Chile',
    CM: 'Cameroun',
    CN: 'Kina',
    CO: 'Colombia',
    CR: 'Costa Rica',
    CU: 'Cuba',
    CV: 'Kap Verde',
    CW: 'Curaçao',
    CX: 'Juleøen',
    CY: 'Cypern',
    CZ: 'Tjekkiet',
    DE: 'Tyskland',
    DJ: 'Djibouti',
    DK: 'Danmark',
    DM: 'Dominica',
    DO: 'Den Dominikanske Republik',
    DZ: 'Algeriet',
    EC: 'Ecuador',
    EE: 'Estland',
    EG: 'Egypten',
    EH: 'Vestsahara',
    ER: 'Eritrea',
    ES: 'Spanien',
    ET: 'Etiopien',
    FI: 'Finland',
    FJ: 'Fiji',
    FK: 'Falklandsøerne',
    FO: 'Færøerne (Føroyar)',
    FR: 'Frankrig',
    GA: 'Gabon',
    GB: 'Det Forenede Kongerige',
    GD: 'Grenada',
    GE: 'Georgien',
    GF: 'Fransk Guyana',
    GG: 'Guernsey',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GL: 'Grønland (Kalaallit Nunaat)',
    GM: 'Gambia',
    GN: 'Guinea',
    GP: 'Guadeloupe',
    GQ: 'Ækvatorialguinea',
    GR: 'Grækenland',
    GS: 'South Georgia og De Sydlige Sandwichøer',
    GT: 'Guatemala',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HK: 'Hongkong',
    HM: 'Heard- og McDonaldøerne',
    HN: 'Honduras',
    HR: 'Kroatien',
    HT: 'Haiti',
    HU: 'Ungarn',
    ID: 'Indonesien',
    IE: 'Irland',
    IL: 'Israel',
    IM: 'Isle of Man',
    IN: 'Indien',
    IO: 'Det Britiske Territorium i Det Indiske Ocean',
    IQ: 'Irak',
    IR: 'Iran',
    IS: 'Island',
    IT: 'Italien',
    JE: 'Jersey',
    JM: 'Jamaica',
    JO: 'Jordan',
    JP: 'Japan',
    KE: 'Kenya',
    KG: 'Kirgisistan',
    KH: 'Cambodja',
    KI: 'Kiribati',
    KM: 'Comorerne',
    KN: 'Saint Kitts og Nevis',
    KP: 'Nordkorea',
    KR: 'Sydkorea',
    KW: 'Kuwait',
    KY: 'Caymanøerne',
    KZ: 'Kasakhstan',
    LA: 'Laos',
    LB: 'Libanon',
    LC: 'Saint Lucia',
    LI: 'Liechtenstein',
    LK: 'Sri Lanka',
    LR: 'Liberia',
    LS: 'Lesotho',
    LT: 'Litauen',
    LU: 'Luxembourg',
    LV: 'Letland',
    LY: 'Libyen',
    MA: 'Marokko',
    MC: 'Monaco',
    MD: 'Moldova',
    ME: 'Montenegro',
    MF: 'St. Martin',
    MG: 'Madagaskar',
    MK: 'Den Tidligere Jugoslaviske Republik Makedonien',
    ML: 'Mali',
    MM: 'Myanmar',
    MN: 'Mongoliet',
    MO: 'Macao',
    MQ: 'Martinique',
    MR: 'Mauretanien',
    MS: 'Montserrat',
    MT: 'Malta',
    MU: 'Mauritius',
    MV: 'Maldiverne',
    MW: 'Malawi',
    MX: 'Mexico',
    MY: 'Malaysia',
    MZ: 'Mozambique',
    NA: 'Namibia',
    NC: 'Ny Kaledonien',
    NE: 'Niger',
    NF: 'Norfolk Island',
    NG: 'Nigeria',
    NI: 'Nicaragua',
    NL: 'Nederlandene',
    NO: 'Norge',
    NP: 'Nepal',
    NR: 'Nauru',
    NU: 'Niue',
    NZ: 'New Zealand',
    OM: 'Oman',
    PA: 'Panama',
    PE: 'Peru',
    PF: 'Fransk Polynesien',
    PG: 'Papua Ny Guinea',
    PH: 'Filippinerne',
    PK: 'Pakistan',
    PL: 'Polen',
    PM: 'Saint Pierre og Miquelon',
    PN: 'Pitcairn',
    PS: 'Palæstinensiske territorier',
    PT: 'Portugal',
    PY: 'Paraguay',
    QA: 'Qatar',
    RE: 'Réunion',
    RO: 'Rumænien',
    RS: 'Serbien',
    RU: 'Rusland',
    RW: 'Rwanda',
    SA: 'Saudi-Arabien',
    SB: 'Salomonøerne',
    SC: 'Seychellerne',
    SD: 'Sudan',
    SE: 'Sverige',
    SG: 'Singapore',
    SH: 'Saint Helena',
    SI: 'Slovenien',
    SJ: 'Svalbard og Jan Mayen',
    SK: 'Slovakiet',
    SL: 'Sierra Leone',
    SM: 'San Marino',
    SN: 'Senegal',
    SO: 'Somalia',
    SR: 'Surinam',
    SS: 'Sydsudan',
    ST: 'São Tomé og Príncipe',
    SV: 'El Salvador',
    SX: 'Sint Maarten',
    SY: 'Syrien',
    SZ: 'Swaziland',
    TA: 'Tristan da Cunha',
    TC: 'Turks- og Caicosøerne',
    TD: 'Tchad',
    TF: 'De Franske Besiddelser i Det Sydlige Indiske Ocean',
    TG: 'Togo',
    TH: 'Thailand',
    TJ: 'Tadsjikistan',
    TK: 'Tokelau',
    TL: 'Østtimor',
    TM: 'Turkmenistan',
    TN: 'Tunesien',
    TO: 'Tonga',
    TR: 'Tyrkiet',
    TT: 'Trinidad og Tobago',
    TV: 'Tuvalu',
    TW: 'Taiwan',
    TZ: 'Tanzania',
    UA: 'Ukraine',
    UG: 'Uganda',
    UM: 'De Mindre Amerikanske Oversøiske Øer',
    US: 'USA',
    UY: 'Uruguay',
    UZ: 'Usbekistan',
    VA: 'Vatikanstaten',
    VC: 'Saint Vincent og Grenadinerne',
    VE: 'Venezuela',
    VG: 'De Britiske Jomfruøer',
    VN: 'Vietnam',
    VU: 'Vanuatu',
    WF: 'Wallis og Futunaøerne',
    WS: 'Samoa',
    XK: 'Kosovo',
    YE: 'Yemen',
    YT: 'Mayotte',
    ZA: 'Sydafrika',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
    ZZ: 'Ukendt region',
  },
}

export const englishCountryNames: CountryNames = {
  AC: 'Ascension Island',
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AF: 'Afghanistan',
  AG: 'Antigua and Barbuda',
  AI: 'Anguilla',
  AL: 'Albania',
  AM: 'Armenia',
  AN: 'Netherlands Antilles',
  AO: 'Angola',
  AR: 'Argentina',
  AT: 'Austria',
  AU: 'Australia',
  AW: 'Aruba',
  AX: 'Åland Islands',
  AZ: 'Azerbaijan',
  BA: 'Bosnia and Herzegovina',
  BB: 'Barbados',
  BD: 'Bangladesh',
  BE: 'Belgium',
  BF: 'Burkina Faso',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BI: 'Burundi',
  BJ: 'Benin',
  BL: 'St. Barthélemy',
  BM: 'Bermuda',
  BN: 'Brunei',
  BO: 'Bolivia',
  BQ: 'Caribbean Netherlands',
  BR: 'Brazil',
  BS: 'The Bahamas',
  BT: 'Bhutan',
  BV: 'Bouvet Island',
  BW: 'Botswana',
  BY: 'Belarus',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Cocos (Keeling) Islands',
  CD: 'Democratic Republic of the Congo',
  CF: 'Central African Republic',
  CG: 'Congo',
  CH: 'Switzerland',
  CI: `Côte d'Ivoire`,
  CK: 'Cook Islands',
  CL: 'Chile',
  CM: 'Cameroon',
  CN: 'China',
  CO: 'Colombia',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cape Verde',
  CW: 'Curaçao',
  CX: 'Christmas Island',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DE: 'Germany',
  DJ: 'Djibouti',
  DK: 'Denmark',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  DZ: 'Algeria',
  EC: 'Ecuador',
  EE: 'Estonia',
  EG: 'Egypt',
  EH: 'Western Sahara',
  ER: 'Eritrea',
  ES: 'Spain',
  ET: 'Ethiopia',
  FI: 'Finland',
  FJ: 'Fiji',
  FK: 'Falkland Islands',
  FO: 'Faeroe Islands',
  FR: 'France',
  GA: 'Gabon',
  GB: 'United Kingdom',
  GD: 'Grenada',
  GE: 'Georgia',
  GF: 'French Guiana',
  GG: 'Guernsey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Greenland',
  GM: 'The Gambia',
  GN: 'Guinea',
  GP: 'Guadeloupe',
  GQ: 'Equatorial Guinea',
  GR: 'Greece',
  GS: 'South Georgia and the South Sandwich Islands',
  GT: 'Guatemala',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HM: 'Heard Island and McDonald Islands',
  HN: 'Honduras',
  HR: 'Croatia',
  HT: 'Haiti',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IM: 'Isle of Man',
  IN: 'India',
  IO: 'British Indian Ocean Territory',
  IQ: 'Iraq',
  IR: 'Iran',
  IS: 'Iceland',
  IT: 'Italy',
  JE: 'Jersey',
  JM: 'Jamaica',
  JO: 'Jordan',
  JP: 'Japan',
  KE: 'Kenya',
  KG: 'Kyrgyzstan',
  KH: 'Cambodia',
  KI: 'Kiribati',
  KM: 'Comoros',
  KN: 'Saint Kitts and Nevis',
  KP: 'North Korea',
  KR: 'South Korea',
  KW: 'Kuwait',
  KY: 'Cayman Islands',
  KZ: 'Kazakhstan',
  LA: 'Laos',
  LB: 'Lebanon',
  LC: 'Saint Lucia',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  LY: 'Libya',
  MA: 'Morocco',
  MC: 'Monaco',
  MD: 'Moldova',
  ME: 'Montenegro',
  MF: 'St. Martin',
  MG: 'Madagascar',
  MK: 'Former Yugoslav Republic of Macedonia',
  ML: 'Mali',
  MM: 'Myanmar',
  MN: 'Mongolia',
  MO: 'Macau',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MS: 'Montserrat',
  MT: 'Malta',
  MU: 'Mauritius',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexico',
  MY: 'Malaysia',
  MZ: 'Mozambique',
  NA: 'Namibia',
  NC: 'New Caledonia',
  NE: 'Niger',
  NF: 'Norfolk Island',
  NG: 'Nigeria',
  NI: 'Nicaragua',
  NL: 'Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'New Zealand',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Peru',
  PF: 'French Polynesia',
  PG: 'Papua New Guinea',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PM: 'Saint Pierre and Miquelon',
  PN: 'Pitcairn Islands',
  PS: 'Palestinian Territories',
  PT: 'Portugal',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'Réunion',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russia',
  RW: 'Rwanda',
  SA: 'Saudi Arabia',
  SB: 'Solomon Islands',
  SC: 'Seychelles',
  SD: 'Sudan',
  SE: 'Sweden',
  SG: 'Singapore',
  SH: 'Saint Helena',
  SI: 'Slovenia',
  SJ: 'Svalbard and Jan Mayen',
  SK: 'Slovakia',
  SL: 'Sierra Leone',
  SM: 'San Marino',
  SN: 'Senegal',
  SO: 'Somalia',
  SR: 'Suriname',
  SS: 'South Sudan',
  ST: 'São Tomé and Príncipe',
  SV: 'El Salvador',
  SX: 'Sint Maarten',
  SY: 'Syria',
  SZ: 'Swaziland',
  TA: 'Tristan da Cunha',
  TC: 'Turks and Caicos Islands',
  TD: 'Chad',
  TF: 'French Southern Territories',
  TG: 'Togo',
  TH: 'Thailand',
  TJ: 'Tajikistan',
  TK: 'Tokelau',
  TL: 'East Timor',
  TM: 'Turkmenistan',
  TN: 'Tunisia',
  TO: 'Tonga',
  TR: 'Turkey',
  TT: 'Trinidad and Tobago',
  TV: 'Tuvalu',
  TW: 'Taiwan',
  TZ: 'Tanzania',
  UA: 'Ukraine',
  UG: 'Uganda',
  UM: 'United States Minor Outlying Islands',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VA: 'Vatican City',
  VC: 'Saint Vincent and the Grenadines',
  VE: 'Venezuela',
  VG: 'British Virgin Islands',
  VN: 'Vietnam',
  VU: 'Vanuatu',
  WF: 'Wallis and Futuna',
  WS: 'Samoa',
  XK: 'Kosovo',
  YE: 'Yemen',
  YT: 'Mayotte',
  ZA: 'South Africa',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
  ZZ: 'Unknown Region',
}
